import { useEffect, useMemo, useRef, useState } from 'react';
import { ChartType } from '../screens/Detail/Charts/ProfitChart';

export type ChartDataPoint = {
  x: number;
  y: number;
};

interface AnimationState {
  data: Array<ChartDataPoint[]>;
  domain: { x: [number, number]; y: [number, number] };
  finished: boolean;
}

/** Largest index `i` in `series` where `series[i].x <= x`, +1 — i.e. the
 *  exclusive end index for `series.slice(0, end)`. Binary search since series
 *  are sorted by x. */
const upperBoundIndex = (series: ChartDataPoint[], x: number): number => {
  let lo = 0;
  let hi = series.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (series[mid].x <= x) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

/**
 * Drives a left-to-right reveal animation over one or more chart datasets.
 *
 *   - Each chart used to spawn its own `setInterval` firing every 3ms (~333
 *     ticks/sec). With many cards on screen that meant 50× the event loop
 *     load + 50× React commits per second. Now uses one `requestAnimationFrame`
 *     per chart instead, frame-aligned and paused when the tab is hidden.
 *   - Per-frame work used `.filter` on every dataset (O(M·N) per tick) plus
 *     `Math.min(...arr)` / `Math.max(...arr)` which allocates per tick. Now
 *     uses binary search for slice indices (O(M·log N)) and explicit
 *     for-loops for min/max.
 *   - When `useAnimatedDomain` is false, the final y/x domain is the same on
 *     every tick — precompute it once and reuse, so the only thing changing
 *     per frame is the visible data length.
 *   - Single state object so each frame commits once instead of twice.
 *
 * `speed` keeps the legacy meaning (higher = slower); total animation duration
 * is `30 * speed * speed` ms, matching the previous tuning.
 */
const useAnimatedChartData = (
  id: string,
  dataPoints: Array<ChartDataPoint[]>,
  selectedChartTypes: ChartType[],
  useAnimatedDomain?: boolean,
  speed = 3,
  disableAnimation = false,
) => {
  const hasData = useMemo(
    () => dataPoints.some((d) => !!d.length),
    [dataPoints],
  );

  // Earliest x value — the dataset with the smallest first x is the one we
  // drive the reveal against.
  const earliestDataSetIndex = useMemo(() => {
    if (!hasData) return 0;
    let bestIdx = 0;
    let bestTs = Infinity;
    for (let i = 0; i < dataPoints.length; i++) {
      const series = dataPoints[i];
      if (!series?.length) continue;
      if (series[0].x < bestTs) {
        bestTs = series[0].x;
        bestIdx = i;
      }
    }
    return bestIdx;
  }, [dataPoints, hasData]);

  // Static domain — computed once per `dataPoints` reference. Used both
  // when animation is disabled and as the steady-state target when
  // `useAnimatedDomain` is false (so we don't recompute it every frame).
  const staticDomain = useMemo<{
    x: [number, number];
    y: [number, number];
  } | null>(() => {
    if (!hasData) return null;
    const earliest = dataPoints[earliestDataSetIndex] ?? [];
    let minX = Infinity;
    let maxX = -Infinity;
    for (const p of earliest) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
    }
    let minY = Infinity;
    let maxY = -Infinity;
    for (const set of dataPoints) {
      for (const p of set) {
        if (p.y < minY) minY = p.y;
        if (p.y > maxY) maxY = p.y;
      }
    }
    if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
      return { x: [0, 0], y: [0, 0] };
    }
    return { x: [minX, maxX], y: [minY, maxY] };
  }, [hasData, dataPoints, earliestDataSetIndex]);

  const emptySlices = useMemo<Array<ChartDataPoint[]>>(
    () => dataPoints.map(() => []),
    [dataPoints],
  );

  const [state, setState] = useState<AnimationState>(() => ({
    data: emptySlices,
    domain: { x: [0, 0], y: [0, 0] },
    finished: false,
  }));

  // Capture the latest mutable refs so the RAF closure doesn't re-create on
  // every render — only animation-restart triggers (id, dataPoints reference,
  // disableAnimation, etc.) should rebind it.
  const restartTokenRef = useRef(0);

  useEffect(() => {
    if (disableAnimation) {
      // Skip animation entirely: snap to the final data + static domain.
      setState({
        data: dataPoints,
        domain: staticDomain ?? { x: [0, 0], y: [0, 0] },
        finished: true,
      });
      return;
    }
    if (!hasData) {
      setState({
        data: emptySlices,
        domain: { x: [0, 0], y: [0, 0] },
        finished: false,
      });
      return;
    }

    const earliest = dataPoints[earliestDataSetIndex];
    if (!earliest?.length) return;

    // Fixed ~1s reveal regardless of how many points the dataset holds —
    // RAF + binary-search slicing makes the cost per frame near-constant.
    // `speed` is kept as a scalar override (default 3 → 1s, 6 → 2s, …) so
    // existing callsites that pass a custom speed still scale predictably,
    // but the total no longer depends on dataset length.
    const BASE_DURATION_MS = 1000;
    const durationMs = Math.max(120, (BASE_DURATION_MS * speed) / 3);
    const startedAt =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const myToken = ++restartTokenRef.current;

    let rafId = 0;

    const tick = (nowArg?: number) => {
      // Cancellation: a later effect rerun bumped the token, so abandon.
      if (myToken !== restartTokenRef.current) return;
      const now =
        nowArg ??
        (typeof performance !== 'undefined' ? performance.now() : Date.now());
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const upTo = Math.max(1, Math.ceil(progress * earliest.length));
      const headX = earliest[upTo - 1].x;
      const startX = earliest[0].x;

      // Per-dataset slice via binary search — way cheaper than .filter on
      // each tick, especially when several charts animate together.
      const slices: Array<ChartDataPoint[]> = new Array(dataPoints.length);
      for (let i = 0; i < dataPoints.length; i++) {
        const series = dataPoints[i];
        if (!series?.length) {
          slices[i] = [];
          continue;
        }
        const end = upperBoundIndex(series, headX);
        slices[i] = end > 0 ? series.slice(0, end) : [];
      }

      let nextDomain: { x: [number, number]; y: [number, number] };
      if (useAnimatedDomain) {
        // Incrementally walk the current slices for y bounds. Avoids the
        // spread-on-array allocation Math.min(...arr) used to do.
        let minY = Infinity;
        let maxY = -Infinity;
        for (const slice of slices) {
          for (const p of slice) {
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
          }
        }
        const firstY = earliest[0].y;
        nextDomain = {
          x: [startX, headX],
          y: [
            Number.isFinite(minY) ? minY : 0,
            Math.max(firstY * 2, Number.isFinite(maxY) ? maxY : 0),
          ],
        };
      } else {
        // Domain is fixed — reuse the precomputed static result.
        nextDomain = staticDomain ?? { x: [0, 0], y: [0, 0] };
      }

      const finished = progress >= 1;
      setState({
        data: finished ? dataPoints : slices,
        domain: nextDomain,
        finished,
      });

      if (!finished) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    // Capture the ref locally so the lint check (and the cleanup itself) is
    // immune to a later increment racing with this teardown.
    const tokenRef = restartTokenRef;
    return () => {
      tokenRef.current++;
      cancelAnimationFrame(rafId);
    };
    // `selectedChartTypes` is intentionally a dep: it restarts the animation
    // when the user toggles which series are visible (existing behaviour).
  }, [
    id,
    dataPoints,
    earliestDataSetIndex,
    hasData,
    useAnimatedDomain,
    speed,
    disableAnimation,
    selectedChartTypes,
    emptySlices,
    staticDomain,
  ]);

  return {
    animatedDataPoints: disableAnimation
      ? dataPoints
      : hasData
      ? state.data
      : [[]],
    domain: disableAnimation ? staticDomain ?? state.domain : state.domain,
    isAnimatedFinished: disableAnimation ? true : state.finished,
  };
};

export default useAnimatedChartData;
